import { Link } from 'react-router-dom'
import { FileText, RotateCcw, Upload, ArrowDownToLine, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/shared/EmptyState'
import { ErrorState } from '@/components/shared/ErrorState'
import {
  useAdminRegulatoryBriefs,
  useDemoteRegulatoryBrief,
  usePublishRegulatoryBrief,
  useRegenerateRegulatoryBrief,
  type AdminRegulatoryBriefListItem,
} from '@/hooks/useAdminRegulatoryBriefs'
import { formatDate } from '@/lib/utils'

const COVERAGE_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  full: 'default',
  partial: 'secondary',
}

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'outline',
  published: 'default',
  archived: 'secondary',
}

function coverageLabel(value: string) {
  return value === 'full' ? 'Full' : 'Partial'
}

function statusLabel(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function lockLabel(brief: AdminRegulatoryBriefListItem) {
  if (brief.lockStatus === 'locked') {
    return brief.lockHolderDisplayName
      ? `Locked by ${brief.lockHolderDisplayName}`
      : 'Locked'
  }
  if (brief.lockStatus === 'expired') return 'Expired lock'
  return 'Unlocked'
}

export function AdminRegulatoryBriefsListPage() {
  const { data, isLoading, isError, refetch } = useAdminRegulatoryBriefs()
  const regenerate = useRegenerateRegulatoryBrief()
  const publish = usePublishRegulatoryBrief()
  const demote = useDemoteRegulatoryBrief()
  const briefs = data?.briefs ?? []

  return (
    <main className="flex flex-col gap-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FileText className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Regulatory briefs</h1>
            <p className="text-sm text-muted-foreground">
              Review generated framework briefs before publishing them to the regulatory surface.
            </p>
          </div>
        </div>
      </header>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState
          title="Failed to load regulatory briefs"
          message="We couldn't reach the admin regulatory briefs API. Try again in a moment."
          onRetry={() => refetch()}
        />
      ) : briefs.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No regulatory briefs yet"
          description="Run a framework brief draft job to populate the admin review queue."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3 font-semibold">Framework</th>
                <th className="p-3 font-semibold">Jurisdiction</th>
                <th className="p-3 font-semibold">Coverage</th>
                <th className="p-3 font-semibold">Review</th>
                <th className="p-3 font-semibold">Last drafted</th>
                <th className="p-3 font-semibold">Lock status</th>
                <th className="p-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {briefs.map((brief) => {
                const fullCoverage = brief.coverageState === 'full'
                return (
                  <tr key={brief.id} className="border-b last:border-b-0 hover:bg-muted/20">
                    <td className="p-3">
                      <div className="font-medium">{brief.frameworkName}</div>
                      <div className="text-xs text-muted-foreground">{brief.frameworkSlug}</div>
                    </td>
                    <td className="p-3 text-muted-foreground">{brief.jurisdiction}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant={COVERAGE_VARIANT[brief.coverageState] ?? 'outline'}>
                          {coverageLabel(brief.coverageState)}
                        </Badge>
                        <Badge variant={STATUS_VARIANT[brief.status] ?? 'outline'}>
                          {statusLabel(brief.status)}
                        </Badge>
                      </div>
                    </td>
                    <td className="p-3">
                      {brief.needsReview ? (
                        <Badge variant="destructive">Needs review</Badge>
                      ) : (
                        <span className="text-muted-foreground">Ready</span>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">{formatDate(brief.lastDraftedAt)}</td>
                    <td className="p-3 text-muted-foreground">{lockLabel(brief)}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/admin/regulatory-briefs/${brief.id}/edit`} aria-label={`Edit ${brief.frameworkName}`}>
                            <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden />
                            Edit
                          </Link>
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => regenerate.mutate(brief.frameworkId)}
                          disabled={regenerate.isPending}
                        >
                          <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden />
                          Regenerate
                        </Button>
                        {fullCoverage ? (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                publish.mutate({ id: brief.id, expectedVersion: brief.version })
                              }
                              disabled={publish.isPending || brief.status !== 'draft'}
                            >
                              <Upload className="mr-1 h-3.5 w-3.5" aria-hidden />
                              Publish
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => demote.mutate(brief.frameworkId)}
                              disabled={demote.isPending || brief.status !== 'published'}
                            >
                              <ArrowDownToLine className="mr-1 h-3.5 w-3.5" aria-hidden />
                              Demote
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
